/**
 * OfferFormModal Component
 * Modal for creating and editing offers with geographic and organization targeting
 */

import { useState, useEffect } from "react";
import type {
  ExtendedOffer,
  CreateOfferDTO,
  UpdateOfferDTO,
  OfferType,
  OfferValueType,
  GeographicScope,
  RedemptionScope,
} from "../../types/program.types";
import type { UUID } from "../../types/common.types";
import { supabase } from "../../services/supabase";
import { fetchAllRows } from "../../utils/fetchAllRows";
import EventPicker from "../events/EventPicker";

interface OfferFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (input: CreateOfferDTO | UpdateOfferDTO) => Promise<void>;
  offer?: ExtendedOffer | null;
  title?: string;
}

// Import from program types
const offerTypeOptions = [
  {
    value: "VOUCHER_CODE",
    label: "Voucher Code",
    description: "Traditional voucher with unique code",
  },
  {
    value: "MEMBER_BENEFIT",
    label: "Member Benefit",
    description: "Always available to members (no code needed)",
  },
  {
    value: "EVENT_REGISTRATION",
    label: "Event Registration",
    description: "Sign up for event",
  },
  {
    value: "SUBSCRIPTION",
    label: "Subscription",
    description: "Upgrade/add service",
  },
  { value: "REFERRAL", label: "Referral", description: "Refer a friend" },
  {
    value: "SURVEY",
    label: "Survey",
    description: "Complete survey for reward",
  },
  {
    value: "EVENT_DRAWING",
    label: "Event Drawing Entry",
    description:
      "Non-monetary reward: entry into an event door-prize drawing (redeemed at the table, no dollar value)",
  },
];

const valueTypeOptions = [
  { value: "PERCENTAGE", label: "Percentage", description: "e.g., 10% off" },
  { value: "FIXED", label: "Fixed Amount", description: "e.g., $50 off" },
  { value: "FREE", label: "Free", description: "Completely free" },
  { value: "BOGO", label: "Buy One Get One", description: "BOGO offer" },
  { value: "CUSTOM", label: "Custom", description: "Custom offer description" },
];

const OFFER_DEFAULT_TIMEZONE = "America/Chicago";

function getTimezoneOffset(timezone: string, dateStr: string): string {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "shortOffset",
  });
  const parts = formatter.formatToParts(new Date(`${dateStr}T12:00:00`));
  const offset = parts.find((part) => part.type === "timeZoneName")?.value;
  const match = offset
    ?.replace("GMT", "")
    .match(/^([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (!match) {
    return "-06:00";
  }
  return `${match[1]}${match[2].padStart(2, "0")}:${match[3] || "00"}`;
}

function dateInputToCentralIso(
  dateStr: string,
  boundary: "start" | "end",
): string | undefined {
  if (!dateStr) {
    return undefined;
  }
  const offset = getTimezoneOffset(OFFER_DEFAULT_TIMEZONE, dateStr);
  const time = boundary === "start" ? "00:00:00" : "23:59:59";
  return new Date(`${dateStr}T${time}${offset}`).toISOString();
}

function isoToCentralDateInput(iso: string | undefined): string {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: OFFER_DEFAULT_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return year && month && day ? `${year}-${month}-${day}` : "";
}

// Escalation window uses date+time. A <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm") is
// interpreted as CENTRAL (the offer default tz) and converted to a UTC ISO instant, and back.
function centralDatetimeInputToIso(dt: string | undefined): string | undefined {
  if (!dt) {
    return undefined;
  }
  const offset = getTimezoneOffset(OFFER_DEFAULT_TIMEZONE, dt.slice(0, 10));
  const withSec = dt.length === 16 ? `${dt}:00` : dt;
  return new Date(`${withSec}${offset}`).toISOString();
}

function isoToCentralDatetimeInput(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: OFFER_DEFAULT_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value;
  const y = get("year");
  const mo = get("month");
  const d = get("day");
  let h = get("hour");
  const mi = get("minute");
  if (h === "24") {
    h = "00"; // en-CA renders midnight as 24
  }
  return y && mo && d && h && mi ? `${y}-${mo}-${d}T${h}:${mi}` : "";
}

// Map offer types to their valid value types
const OFFER_VALUE_TYPE_MAP: Record<string, string[]> = {
  // Voucher-code offers carry a FIXED dollar value that downstream voucher
  // generation (generate-offer / generate-flight-vouchers) reads from
  // offers.value_amount. Generation treats the value as a dollar discount, so
  // only FIXED is offered here.
  VOUCHER_CODE: ["FIXED"],
  MEMBER_BENEFIT: ["PERCENTAGE", "FIXED", "FREE", "CUSTOM"],
  EVENT_REGISTRATION: ["FREE", "FIXED"],
  SUBSCRIPTION: ["PERCENTAGE", "FIXED", "FREE"],
  REFERRAL: ["FIXED", "PERCENTAGE", "CUSTOM"],
  SURVEY: ["FREE", "CUSTOM"],
  // Event drawing entries are non-monetary — always FREE ($0). The DB CHECK enforces this.
  EVENT_DRAWING: ["FREE"],
};

// Value category options for MEMBER_BENEFIT type
const VALUE_CATEGORY_OPTIONS = [
  { value: "Training", label: "Training" },
  { value: "Range Time", label: "Range Time" },
  { value: "Gear", label: "Gear" },
  { value: "Service", label: "Service" },
  { value: "Hunting & Fishing", label: "Hunting & Fishing" },
];

const scopeOptions = [
  {
    value: "NATIONAL",
    label: "National",
    description: "Available in all states",
  },
  {
    value: "MULTI_STATE",
    label: "Multi-State",
    description: "Available in specific states",
  },
  {
    value: "SINGLE_STATE",
    label: "Single State",
    description: "Available in one state only",
  },
];

const redemptionScopeOptions = [
  {
    value: "SINGLE_ORGANIZATION",
    label: "Single Organization",
    description: "Redeemable at one specific organization (legacy behavior)",
  },
  {
    value: "MULTIPLE_ORGANIZATIONS",
    label: "Multiple Organizations",
    description: "Redeemable at selected organizations or by type",
  },
  {
    value: "ALL_ORGANIZATIONS",
    label: "All Organizations",
    description: "Redeemable at any organization",
  },
];

// Organization types — matches ORGANIZATION_TYPE_OPTIONS from organization.types.ts
const organizationTypeOptions = [
  { value: "retailer", label: "Retailer" },
  { value: "retailer_with_range", label: "Retailer with Range" },
  { value: "range", label: "Range" },
  { value: "instructor", label: "Instructor" },
  { value: "promoter", label: "Promoter" },
  { value: "organization", label: "Organization" },
  { value: "manufacturer", label: "Manufacturer" },
  { value: "media", label: "Media" },
  { value: "partner", label: "Partner" },
  { value: "business", label: "Business" },
  { value: "other", label: "Other" },
];

const usStates = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

export default function OfferFormModal({
  isOpen,
  onClose,
  onSave,
  offer,
  title,
}: OfferFormModalProps) {
  // Basic fields
  const [offerCode, setOfferCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [offerType, setOfferType] = useState<OfferType>("VOUCHER_CODE");
  const [valueType, setValueType] = useState<OfferValueType>("FIXED");
  const [valueAmount, setValueAmount] = useState<string>("");
  const [valueDescription, setValueDescription] = useState("");
  const [valueCategory, setValueCategory] = useState("");
  const [isFeatured, setIsFeatured] = useState(true);
  const [monetaryValue, setMonetaryValue] = useState<string>("");
  // Event Ticket reward "$0 placeholder" — mints a code-only voucher whose value is assigned later.
  const [isPlaceholder, setIsPlaceholder] = useState(false);
  // Event Ticket binding — a $0 placeholder offer MUST reference the event it admits to.
  const [eventId, setEventId] = useState<string>("");

  // Event Ticket auto-escalation config (only shown when this offer is a $0 placeholder/ticket offer).
  const [autoEscalate, setAutoEscalate] = useState(false);
  const [escalationOfferId, setEscalationOfferId] = useState<string>("");
  const [escalationStartMode, setEscalationStartMode] = useState<
    "immediate" | "fixed"
  >("immediate");
  const [escalationStartAt, setEscalationStartAt] = useState<string>(""); // datetime-local, Central
  const [escalationEndMode, setEscalationEndMode] = useState<
    "end_of_day" | "fixed"
  >("end_of_day");
  const [escalationEndAt, setEscalationEndAt] = useState<string>(""); // datetime-local, Central
  // Award offers assignable as the escalation target (active, non-placeholder, positive value).
  const [awardOffers, setAwardOffers] = useState<
    Array<{ id: string; name: string; value_amount: number | null }>
  >([]);

  // Geographic
  const [geographicScope, setGeographicScope] =
    useState<GeographicScope>("NATIONAL");
  const [targetStates, setTargetStates] = useState<string[]>([]);

  // Redemption scope - which organizations can redeem
  const [redemptionScope, setRedemptionScope] =
    useState<RedemptionScope>("ALL_ORGANIZATIONS");
  const [targetOrganizationIds, setTargetOrganizationIds] = useState<UUID[]>(
    [],
  );
  const [targetLocationId, setTargetLocationId] = useState<UUID | "">("");
  const [allowedOrgTypes, setAllowedOrgTypes] = useState<string[]>([]);
  const [organizations, setOrganizations] = useState<
    Array<{ id: UUID; name: string; organization_types: string[] }>
  >([]);
  const [locations, setLocations] = useState<
    Array<{ id: UUID; name: string; city?: string; state?: string }>
  >([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [orgSearch, setOrgSearch] = useState("");

  // Redemption
  const [requiresVoucher, setRequiresVoucher] = useState(true);
  const [maxRedemptionsPerMember, setMaxRedemptionsPerMember] =
    useState<string>("");
  const [maxTotalRedemptions, setMaxTotalRedemptions] = useState<string>("");

  // Validity
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [voucherExpiryDays, setVoucherExpiryDays] = useState("30");
  const [voucherExpiresAtOfferValidTo, setVoucherExpiresAtOfferValidTo] =
    useState(false);

  // Additional
  const [redemptionInstructions, setRedemptionInstructions] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [isActive, setIsActive] = useState(true);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showStateSelector, setShowStateSelector] = useState(false);

  // Determine if value type section should be shown based on offer type
  const isVoucherCode = offerType === "VOUCHER_CODE";
  const validValueTypes = OFFER_VALUE_TYPE_MAP[offerType] || [];
  const filteredValueTypeOptions = valueTypeOptions.filter((opt) =>
    validValueTypes.includes(opt.value),
  );
  // Whether this offer type carries a value (type + amount). VOUCHER_CODE now
  // does (FIXED $ amount used by voucher generation), so this is no longer
  // keyed off isVoucherCode.
  const showValueFields = validValueTypes.length > 0;

  // Show value category for MEMBER_BENEFIT and REFERRAL
  const showValueCategory =
    offerType === "MEMBER_BENEFIT" || offerType === "REFERRAL";

  // Load organizations for org selection (SINGLE or MULTIPLE)
  useEffect(() => {
    if (
      isOpen &&
      (redemptionScope === "MULTIPLE_ORGANIZATIONS" ||
        redemptionScope === "SINGLE_ORGANIZATION") &&
      organizations.length === 0
    ) {
      loadOrganizations();
    }
  }, [isOpen, redemptionScope]);

  // Load assignable award offers for the escalation picker when the placeholder toggle is on.
  useEffect(() => {
    if (isOpen && isPlaceholder && awardOffers.length === 0) {
      loadAwardOffers();
    }
  }, [isOpen, isPlaceholder]);

  // Load locations when a single org is selected
  useEffect(() => {
    if (
      redemptionScope === "SINGLE_ORGANIZATION" &&
      targetOrganizationIds.length === 1
    ) {
      loadLocations(targetOrganizationIds[0]);
    } else {
      setLocations([]);
      setTargetLocationId("");
    }
  }, [targetOrganizationIds, redemptionScope]);

  // When offer type changes, reset value type if current selection is invalid
  // for the new type. VOUCHER_CODE allows only FIXED, so switching to it from a
  // type whose value was e.g. PERCENTAGE coerces back to FIXED.
  useEffect(() => {
    const valid = OFFER_VALUE_TYPE_MAP[offerType] || [];
    if (valid.length > 0 && !valid.includes(valueType)) {
      setValueType(valid[0] as OfferValueType);
    }
  }, [offerType]);

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      // Page through ALL organizations. A single `.limit(10000)` does NOT lift
      // the row cap: PostgREST caps every response at the project's API
      // max-rows (1000), so a one-shot query silently returned only the first
      // 1000 orgs alphabetically — hiding everything later in the alphabet
      // (e.g. "Worth A Shot Inc", rank ~2634 of ~2658). Fetching in pages of
      // 1000 via `.range()` (each page within the cap) returns the full set.
      const all = await fetchAllRows<{
        id: UUID;
        name: string;
        organization_types: string[];
      }>((from, to) =>
        supabase
          .from("organizations")
          .select("id, name, organization_types")
          .eq("is_archived", false)
          .order("name")
          .range(from, to),
      );
      setOrganizations(all);
    } catch (error) {
      console.error("Error loading organizations:", error);
    } finally {
      setLoadingOrgs(false);
    }
  };

  // Assignable award offers (active, non-placeholder, positive value) for the escalation target.
  const loadAwardOffers = async () => {
    try {
      const { data } = await supabase
        .from("offers")
        .select("id, name, value_amount")
        .eq("is_active", true)
        // `status` is a sparse text column that is NULL on most offers (including every
        // offer created through this modal — the real active flag is the boolean
        // is_active). Treat NULL as active so a freshly-created award (e.g. an Event
        // Drawing offer) is selectable. Mirrors ticketRewards.getAssignableOffers.
        .or("status.is.null,status.eq.active")
        .or("is_placeholder.is.null,is_placeholder.eq.false")
        // A reward award may be a positive-value offer OR a designated non-monetary reward
        // (e.g. an Event Drawing Entry). Matches _assign_ticket_reward_core's relaxed guard.
        .or("value_amount.gt.0,is_non_monetary_reward.eq.true")
        .order("name")
        .limit(500);
      setAwardOffers(
        (data as Array<{
          id: string;
          name: string;
          value_amount: number | null;
        }>) || [],
      );
    } catch (error) {
      console.error("Error loading award offers:", error);
    }
  };

  const loadLocations = async (orgId: UUID) => {
    setLoadingLocations(true);
    try {
      const { data, error } = await supabase
        .from("locations")
        .select("id, name, city, state")
        .eq("organization_id", orgId)
        .order("name");

      if (error) {
        throw error;
      }
      setLocations(data || []);
    } catch (error) {
      console.error("Error loading locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Initialize form when editing
  useEffect(() => {
    if (offer) {
      setOfferCode(offer.offer_code || "");
      setName(offer.name || "");
      setDescription(offer.description || "");
      setOfferType(offer.offer_type || "VOUCHER_CODE");
      setValueType(offer.value_type || "FIXED");
      setValueAmount(offer.value_amount?.toString() || "");
      setValueDescription(offer.value_description || "");
      setValueCategory(offer.value_category || "");
      setIsFeatured(offer.is_featured ?? true);
      setIsPlaceholder(
        (offer as { is_placeholder?: boolean }).is_placeholder ?? false,
      );
      setEventId((offer as { event_id?: string | null }).event_id ?? "");
      setMonetaryValue(offer.monetary_value?.toString() || "");
      setGeographicScope(offer.geographic_scope || "NATIONAL");
      setTargetStates(offer.allowed_states || []);
      setRequiresVoucher(offer.requires_voucher ?? true);
      setMaxRedemptionsPerMember(
        offer.max_redemptions_per_member?.toString() || "",
      );
      setMaxTotalRedemptions(offer.max_total_redemptions?.toString() || "");
      setValidFrom(isoToCentralDateInput(offer.valid_from));
      setValidTo(isoToCentralDateInput(offer.valid_to));
      setVoucherExpiryDays(offer.voucher_expiry_days?.toString() || "30");
      setVoucherExpiresAtOfferValidTo(
        offer.voucher_expires_at_offer_valid_to ?? false,
      );
      setRedemptionInstructions(offer.redemption_instructions || "");
      setTermsAndConditions(offer.terms_and_conditions || "");
      setIsActive(offer.is_active ?? true);
      // Redemption scope
      setRedemptionScope(offer.redemption_scope || "ALL_ORGANIZATIONS");
      setTargetOrganizationIds(offer.allowed_organization_ids || []);
      setAllowedOrgTypes(offer.allowed_organization_types || []);
      // Event Ticket auto-escalation
      const esc = offer as {
        auto_escalate?: boolean;
        escalation_offer_id?: string | null;
        escalation_start_mode?: string;
        escalation_start_at?: string | null;
        escalation_end_mode?: string;
        escalation_end_at?: string | null;
      };
      setAutoEscalate(esc.auto_escalate ?? false);
      setEscalationOfferId(esc.escalation_offer_id ?? "");
      setEscalationStartMode(
        esc.escalation_start_mode === "fixed" ? "fixed" : "immediate",
      );
      setEscalationStartAt(isoToCentralDatetimeInput(esc.escalation_start_at));
      setEscalationEndMode(
        esc.escalation_end_mode === "fixed" ? "fixed" : "end_of_day",
      );
      setEscalationEndAt(isoToCentralDatetimeInput(esc.escalation_end_at));
    } else {
      // Reset form for new offer
      resetForm();
    }
    setErrors({});
  }, [offer, isOpen]);

  const resetForm = () => {
    setOfferCode("");
    setName("");
    setDescription("");
    setOfferType("VOUCHER_CODE");
    setValueType("FIXED");
    setValueAmount("");
    setValueDescription("");
    setValueCategory("");
    setIsFeatured(true);
    setIsPlaceholder(false);
    setEventId("");
    setMonetaryValue("");
    setGeographicScope("NATIONAL");
    setTargetStates([]);
    setRequiresVoucher(true);
    setMaxRedemptionsPerMember("");
    setMaxTotalRedemptions("");
    setValidFrom("");
    setValidTo("");
    setVoucherExpiryDays("30");
    setVoucherExpiresAtOfferValidTo(false);
    setRedemptionInstructions("");
    setTermsAndConditions("");
    setIsActive(true);
    // Redemption scope - default to all organizations for new offers
    setRedemptionScope("ALL_ORGANIZATIONS");
    setAutoEscalate(false);
    setEscalationOfferId("");
    setEscalationStartMode("immediate");
    setEscalationStartAt("");
    setEscalationEndMode("end_of_day");
    setEscalationEndAt("");
    setTargetOrganizationIds([]);
    setTargetLocationId("");
    setAllowedOrgTypes([]);
    setOrgSearch("");
    setLocations([]);
  };

  // Generate offer code from name
  const generateOfferCode = () => {
    if (!name) {
      return;
    }
    const code = name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "-")
      .replace(/-+/g, "-")
      .substring(0, 20);
    setOfferCode(code);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!offerCode.trim()) {
      newErrors.offerCode = "Offer code is required";
    }

    if (!name.trim()) {
      newErrors.name = "Name is required";
    }

    if (geographicScope !== "NATIONAL" && targetStates.length === 0) {
      newErrors.states = "Select at least one state for non-national offers";
    }

    // Require a value amount whenever the offer type carries a value and the
    // value type implies a numeric amount. Covers VOUCHER_CODE (FIXED) so an
    // existing voucher offer with no value can't be saved/generated as $0
    // without the operator seeing the missing field.
    if (
      !isPlaceholder &&
      showValueFields &&
      valueType !== "FREE" &&
      valueType !== "CUSTOM" &&
      !valueAmount
    ) {
      newErrors.valueAmount = "Value amount is required";
    }

    // An Event Ticket ($0 placeholder) reward must be bound to an event — this
    // mirrors the DB CHECK constraint (is_placeholder ⇒ event_id NOT NULL).
    if (isPlaceholder && !eventId) {
      newErrors.eventId = "An Event Ticket reward must be bound to an event.";
    }

    // Auto-escalation config (mirrors the offers_escalation_* CHECK constraints).
    if (isPlaceholder && autoEscalate && !escalationOfferId) {
      newErrors.escalationOffer =
        "Choose the award to escalate to when auto-escalation is on.";
    }
    if (
      isPlaceholder &&
      escalationStartMode === "fixed" &&
      !escalationStartAt
    ) {
      newErrors.escalationStartAt = "Set the escalation start date & time.";
    }
    if (isPlaceholder && escalationEndMode === "fixed" && !escalationEndAt) {
      newErrors.escalationEndAt = "Set the escalation end date & time.";
    }

    if (validFrom && validTo && new Date(validFrom) > new Date(validTo)) {
      newErrors.validTo = "End date must be after start date";
    }

    if (isVoucherCode && voucherExpiresAtOfferValidTo && !validTo) {
      newErrors.validTo =
        "Valid To is required when voucher expiry uses the offer end date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setIsSaving(true);
    try {
      const input: CreateOfferDTO | UpdateOfferDTO = {
        offer_code: offerCode.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        offer_type: offerType,
        // Persist value_type/value_amount whenever the offer type carries a
        // value — including VOUCHER_CODE, whose FIXED value_amount is what
        // voucher generation reads (offers.value_amount). No longer stripped.
        // EVENT_DRAWING is a designated NON-MONETARY reward — always FREE/$0 (DB CHECK enforces it).
        value_type:
          offerType === "EVENT_DRAWING"
            ? "FREE"
            : showValueFields
              ? valueType
              : undefined,
        // A "$0 placeholder" reward envelope, and any non-monetary EVENT_DRAWING, carry 0 value.
        value_amount:
          isPlaceholder || offerType === "EVENT_DRAWING"
            ? 0
            : showValueFields && valueAmount
              ? parseFloat(valueAmount)
              : undefined,
        // Explicit, server-trusted flag that gates this offer as a redeemable non-monetary reward.
        is_non_monetary_reward: offerType === "EVENT_DRAWING",
        value_description: showValueFields
          ? valueDescription.trim() || undefined
          : undefined,
        value_category: showValueCategory
          ? valueCategory || undefined
          : undefined,
        is_featured: isFeatured,
        is_placeholder: isPlaceholder,
        // Always send the chosen event binding (null when none) so an Event
        // Ticket offer satisfies the is_placeholder ⇒ event_id CHECK constraint.
        event_id: eventId || null,
        // Event Ticket auto-escalation (only meaningful on a placeholder/ticket offer). Datetimes are
        // entered as Central and converted to UTC ISO. Gated to satisfy the offers_escalation_* CHECKs.
        auto_escalate: isPlaceholder ? autoEscalate : false,
        escalation_offer_id:
          isPlaceholder && autoEscalate ? escalationOfferId || null : null,
        escalation_start_mode: escalationStartMode,
        escalation_start_at:
          escalationStartMode === "fixed"
            ? (centralDatetimeInputToIso(escalationStartAt) ?? null)
            : null,
        escalation_end_mode: escalationEndMode,
        escalation_end_at:
          escalationEndMode === "fixed"
            ? (centralDatetimeInputToIso(escalationEndAt) ?? null)
            : null,
        monetary_value:
          !isVoucherCode && monetaryValue
            ? parseFloat(monetaryValue)
            : undefined,
        geographic_scope: geographicScope,
        requires_voucher: requiresVoucher,
        max_redemptions_per_member: maxRedemptionsPerMember
          ? parseInt(maxRedemptionsPerMember)
          : undefined,
        max_total_redemptions: maxTotalRedemptions
          ? parseInt(maxTotalRedemptions)
          : undefined,
        valid_from: dateInputToCentralIso(validFrom, "start"),
        valid_to: dateInputToCentralIso(validTo, "end"),
        voucher_expiry_days:
          isVoucherCode && !voucherExpiresAtOfferValidTo && voucherExpiryDays
            ? parseInt(voucherExpiryDays, 10)
            : undefined,
        voucher_expires_at_offer_valid_to:
          isVoucherCode && voucherExpiresAtOfferValidTo,
        redemption_instructions: redemptionInstructions.trim() || undefined,
        terms_and_conditions: termsAndConditions.trim() || undefined,
        is_active: isActive,
        target_states:
          geographicScope !== "NATIONAL" ? targetStates : undefined,
        // Redemption scope
        redemption_scope: redemptionScope,
        allowed_organization_types:
          redemptionScope === "ALL_ORGANIZATIONS" && allowedOrgTypes.length > 0
            ? allowedOrgTypes
            : undefined,
        target_organization_ids:
          (redemptionScope === "MULTIPLE_ORGANIZATIONS" ||
            redemptionScope === "SINGLE_ORGANIZATION") &&
          targetOrganizationIds.length > 0
            ? targetOrganizationIds
            : undefined,
        target_location_id:
          redemptionScope === "SINGLE_ORGANIZATION" && targetLocationId
            ? (targetLocationId as UUID)
            : undefined,
      };

      await onSave(input);
      onClose();
    } catch (error) {
      console.error("Error saving offer:", error);
      // Surface a duplicate offer code (Postgres unique_violation, HTTP 409) as
      // a field-level error on the Offer Code input, like other validation —
      // instead of only logging the 409 to the console.
      const err = error as {
        code?: string;
        message?: string;
        details?: string;
      };
      const haystack =
        `${err?.message ?? ""} ${err?.details ?? ""}`.toLowerCase();
      if (err?.code === "23505" && haystack.includes("offer_code")) {
        setErrors({
          offerCode:
            "This offer code is already in use. Choose a different one.",
        });
      } else if (err?.code === "23505") {
        setErrors({
          submit:
            "A record with these details already exists. Please review and try again.",
        });
      } else {
        setErrors({ submit: "Failed to save offer. Please try again." });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const toggleState = (code: string) => {
    setTargetStates((prev) =>
      prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code],
    );
  };

  const selectAllStates = () => {
    setTargetStates(usStates.map((s) => s.code));
  };

  const clearAllStates = () => {
    setTargetStates([]);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {title || (offer ? "Edit Offer" : "Create Offer")}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Offer Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Code <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={offerCode}
                    onChange={(e) => setOfferCode(e.target.value.toUpperCase())}
                    className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.offerCode ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="OFFER-CODE"
                    disabled={!!offer} // Can't change code when editing
                  />
                  {!offer && (
                    <button
                      type="button"
                      onClick={generateOfferCode}
                      className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                      title="Generate from name"
                    >
                      Generate
                    </button>
                  )}
                </div>
                {errors.offerCode && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.offerCode}
                  </p>
                )}
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter offer name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Describe the offer..."
              />
            </div>

            {/* Offer Type and Value Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Type
                </label>
                <select
                  value={offerType}
                  onChange={(e) => setOfferType(e.target.value as OfferType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {offerTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Value Type — shown for any offer type that carries a value,
                  including VOUCHER_CODE (FIXED only). */}
              {showValueFields && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value Type
                  </label>
                  <select
                    value={valueType}
                    onChange={(e) =>
                      setValueType(e.target.value as OfferValueType)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {filteredValueTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {isVoucherCode && (
                    <p className="mt-1 text-xs text-gray-500">
                      Generated vouchers from this offer use this dollar value.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* "$0 placeholder" reward envelope (Event Tickets). When on, the
                offer mints a code-only voucher that proves a check-in but holds
                no redeemable value until an offer/value is assigned later
                (assign_ticket_reward). Value amount is forced to $0. */}
            {showValueFields && (
              <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="isPlaceholder"
                    checked={isPlaceholder}
                    onChange={(e) => setIsPlaceholder(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-gray-800">
                      $0 placeholder reward (Event Ticket)
                    </span>
                    <span className="block text-gray-600">
                      Mints a valid voucher that proves a check-in but carries
                      no redeemable value. A real offer/value is assigned later.
                    </span>
                  </span>
                </label>

                {/* Event Ticket binding — a $0 placeholder offer MUST reference
                    the event it admits to (DB CHECK: is_placeholder ⇒ event_id).
                    Server-side type-ahead (shared EventPicker) — no fixed row window. */}
                {isPlaceholder && (
                  <div className="mt-3">
                    <label
                      htmlFor="offer-ticket-event"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Event <span className="text-red-500">*</span>
                    </label>
                    <EventPicker
                      inputId="offer-ticket-event"
                      testId="offer-event-picker"
                      value={eventId}
                      onChange={(id) => setEventId(id)}
                      required
                      error={errors.eventId}
                    />
                  </div>
                )}

                {/* Auto-escalation: on check-in, assign an award and open its redeem window. */}
                {isPlaceholder && (
                  <div className="mt-3 border-t border-indigo-200 pt-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoEscalate}
                        onChange={(e) => setAutoEscalate(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm">
                        <span className="font-medium text-gray-800">
                          Auto-escalate on check-in
                        </span>
                        <span className="block text-gray-600">
                          When the holder checks in, automatically assign an
                          award and open its redemption window.
                        </span>
                      </span>
                    </label>

                    {autoEscalate && (
                      <div className="mt-3 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Escalate to award{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={escalationOfferId}
                            onChange={(e) =>
                              setEscalationOfferId(e.target.value)
                            }
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                              errors.escalationOffer
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            <option value="">Select an award…</option>
                            {awardOffers.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.name}
                                {o.value_amount ? ` — $${o.value_amount}` : ""}
                              </option>
                            ))}
                          </select>
                          {errors.escalationOffer && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.escalationOffer}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Redeemable from
                            </label>
                            <select
                              value={escalationStartMode}
                              onChange={(e) =>
                                setEscalationStartMode(
                                  e.target.value as "immediate" | "fixed",
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="immediate">
                                Immediately on check-in
                              </option>
                              <option value="fixed">At a set time</option>
                            </select>
                            {escalationStartMode === "fixed" && (
                              <>
                                <input
                                  type="datetime-local"
                                  value={escalationStartAt}
                                  onChange={(e) =>
                                    setEscalationStartAt(e.target.value)
                                  }
                                  className={`mt-2 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                    errors.escalationStartAt
                                      ? "border-red-500"
                                      : "border-gray-300"
                                  }`}
                                />
                                {errors.escalationStartAt && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.escalationStartAt}
                                  </p>
                                )}
                              </>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Redeemable until
                            </label>
                            <select
                              value={escalationEndMode}
                              onChange={(e) =>
                                setEscalationEndMode(
                                  e.target.value as "end_of_day" | "fixed",
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="end_of_day">
                                End of the check-in day (11:59 PM)
                              </option>
                              <option value="fixed">At a set time</option>
                            </select>
                            {escalationEndMode === "fixed" && (
                              <>
                                <input
                                  type="datetime-local"
                                  value={escalationEndAt}
                                  onChange={(e) =>
                                    setEscalationEndAt(e.target.value)
                                  }
                                  className={`mt-2 w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                    errors.escalationEndAt
                                      ? "border-red-500"
                                      : "border-gray-300"
                                  }`}
                                />
                                {errors.escalationEndAt && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.escalationEndAt}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">
                          Times are in Central (CT).
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Value Amount and Description — shown for any value-carrying
                offer type, including VOUCHER_CODE (FIXED $ amount). */}
            {showValueFields && !isPlaceholder && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {valueType !== "FREE" && valueType !== "CUSTOM" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Value Amount{" "}
                      {valueType !== "BOGO" && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="relative">
                      {valueType === "FIXED" && (
                        <span className="absolute left-3 top-2 text-gray-500">
                          $
                        </span>
                      )}
                      <input
                        type="number"
                        step={valueType === "PERCENTAGE" ? "1" : "0.01"}
                        value={valueAmount}
                        onChange={(e) => setValueAmount(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          valueType === "FIXED" ? "pl-7" : ""
                        } ${errors.valueAmount ? "border-red-500" : "border-gray-300"}`}
                        placeholder={
                          valueType === "PERCENTAGE"
                            ? "e.g., 10"
                            : "e.g., 50.00"
                        }
                      />
                      {valueType === "PERCENTAGE" && (
                        <span className="absolute right-3 top-2 text-gray-500">
                          %
                        </span>
                      )}
                    </div>
                    {errors.valueAmount && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.valueAmount}
                      </p>
                    )}
                  </div>
                )}

                {(valueType === "CUSTOM" || valueType === "BOGO") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Value Description
                    </label>
                    <input
                      type="text"
                      value={valueDescription}
                      onChange={(e) => setValueDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., Buy 2 get 1 free"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Value Category - for MEMBER_BENEFIT and REFERRAL */}
            {showValueCategory && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value Category
                </label>
                <select
                  value={valueCategory}
                  onChange={(e) => setValueCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select a category...</option>
                  {VALUE_CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Monetary Value - for non-voucher offers (for redemption tracking) */}
            {!isVoucherCode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monetary Value (for redemption tracking)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={monetaryValue}
                    onChange={(e) => {
                      // Strip everything except digits and one decimal point
                      let val = e.target.value.replace(/[^0-9.]/g, "");
                      // Only allow one decimal point
                      const parts = val.split(".");
                      if (parts.length > 2) {
                        val = parts[0] + "." + parts.slice(1).join("");
                      }
                      // Limit to 2 decimal places
                      if (parts.length === 2 && parts[1].length > 2) {
                        val = parts[0] + "." + parts[1].slice(0, 2);
                      }
                      setMonetaryValue(val);
                    }}
                    onBlur={() => {
                      if (monetaryValue && !isNaN(Number(monetaryValue))) {
                        setMonetaryValue(Number(monetaryValue).toFixed(2));
                      } else if (monetaryValue) {
                        setMonetaryValue("");
                      }
                    }}
                    className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Optional - value attributed to each redemption"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Used to calculate contact redemption totals when this offer is
                  redeemed
                </p>
              </div>
            )}

            {/* Geographic Scope */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Geographic Scope
              </label>
              <select
                value={geographicScope}
                onChange={(e) =>
                  setGeographicScope(e.target.value as GeographicScope)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {scopeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} - {opt.description}
                  </option>
                ))}
              </select>
            </div>

            {/* State Selector (for non-national) */}
            {geographicScope !== "NATIONAL" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Target States{" "}
                    {targetStates.length > 0 &&
                      `(${targetStates.length} selected)`}
                  </label>
                  <div className="space-x-2">
                    <button
                      type="button"
                      onClick={selectAllStates}
                      className="text-xs text-primary-600 hover:text-primary-800"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={clearAllStates}
                      className="text-xs text-gray-600 hover:text-gray-800"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div
                  className={`border rounded-md p-3 ${errors.states ? "border-red-500" : "border-gray-300"}`}
                >
                  <button
                    type="button"
                    onClick={() => setShowStateSelector(!showStateSelector)}
                    className="w-full text-left text-sm text-gray-600 hover:text-gray-900"
                  >
                    {targetStates.length === 0
                      ? "Click to select states..."
                      : targetStates.join(", ")}
                  </button>
                  {showStateSelector && (
                    <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-48 overflow-y-auto">
                      {usStates.map((state) => (
                        <label
                          key={state.code}
                          className={`flex items-center p-1 rounded cursor-pointer text-sm ${
                            targetStates.includes(state.code)
                              ? "bg-primary-100 text-primary-800"
                              : "hover:bg-gray-100"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={targetStates.includes(state.code)}
                            onChange={() => toggleState(state.code)}
                            className="sr-only"
                          />
                          <span>{state.code}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {errors.states && (
                  <p className="mt-1 text-sm text-red-600">{errors.states}</p>
                )}
              </div>
            )}

            {/* Redemption Scope - Where can this offer be redeemed? */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Redemption Locations
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Specify which organizations can redeem vouchers from this offer
              </p>

              <div className="space-y-4">
                {/* Redemption Scope Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Redemption Scope
                  </label>
                  <select
                    value={redemptionScope}
                    onChange={(e) => {
                      const newScope = e.target.value as RedemptionScope;
                      setRedemptionScope(newScope);
                      // Reset org selections when switching scope
                      if (newScope !== redemptionScope) {
                        setTargetOrganizationIds([]);
                        setTargetLocationId("");
                        setOrgSearch("");
                      }
                      // Clear org type filter when switching to MULTIPLE (uses junction table, not type filter)
                      if (newScope === "MULTIPLE_ORGANIZATIONS") {
                        setAllowedOrgTypes([]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {redemptionScopeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} - {opt.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Organization Type Filter (only for ALL_ORGANIZATIONS — MULTIPLE uses junction table) */}
                {redemptionScope !== "SINGLE_ORGANIZATION" && (
                  <div
                    className={
                      redemptionScope === "MULTIPLE_ORGANIZATIONS"
                        ? "opacity-50"
                        : ""
                    }
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filter by Organization Type{" "}
                      {redemptionScope === "ALL_ORGANIZATIONS"
                        ? "(optional)"
                        : ""}
                    </label>
                    {redemptionScope === "MULTIPLE_ORGANIZATIONS" && (
                      <p className="text-xs text-gray-500 mb-2">
                        Not applicable — use the organization list below to
                        select specific organizations.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {organizationTypeOptions.map((opt) => (
                        <label
                          key={opt.value}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                            redemptionScope === "MULTIPLE_ORGANIZATIONS"
                              ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                              : allowedOrgTypes.includes(opt.value)
                                ? "bg-primary-100 text-primary-800 border border-primary-300 cursor-pointer"
                                : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 cursor-pointer"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={allowedOrgTypes.includes(opt.value)}
                            disabled={
                              redemptionScope === "MULTIPLE_ORGANIZATIONS"
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAllowedOrgTypes([
                                  ...allowedOrgTypes,
                                  opt.value,
                                ]);
                              } else {
                                setAllowedOrgTypes(
                                  allowedOrgTypes.filter(
                                    (t) => t !== opt.value,
                                  ),
                                );
                              }
                            }}
                            className="sr-only"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                    {redemptionScope === "ALL_ORGANIZATIONS" && (
                      <p className="text-xs text-gray-500 mt-1">
                        Leave empty to allow all organization types
                      </p>
                    )}
                  </div>
                )}

                {/* Single Organization Picker */}
                {redemptionScope === "SINGLE_ORGANIZATION" && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Organization
                      </label>
                      {loadingOrgs ? (
                        <p className="text-sm text-gray-500">
                          Loading organizations...
                        </p>
                      ) : (
                        <>
                          <input
                            type="text"
                            value={orgSearch}
                            onChange={(e) => setOrgSearch(e.target.value)}
                            placeholder="Search organizations..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                          />
                          {/* Show selected org */}
                          {targetOrganizationIds.length === 1 && (
                            <div className="flex items-center justify-between bg-primary-50 px-3 py-2 rounded-md mb-2">
                              <span className="text-sm text-primary-800">
                                {organizations.find(
                                  (o) => o.id === targetOrganizationIds[0],
                                )?.name || "Selected organization"}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setTargetOrganizationIds([]);
                                  setTargetLocationId("");
                                }}
                                className="text-primary-600 hover:text-primary-800 text-sm"
                              >
                                Clear
                              </button>
                            </div>
                          )}
                          {/* Org list (only show when none selected or searching) */}
                          {targetOrganizationIds.length === 0 && (
                            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                              {organizations
                                .filter((org) =>
                                  org.name
                                    .toLowerCase()
                                    .includes(orgSearch.toLowerCase()),
                                )
                                .slice(0, 50)
                                .map((org) => (
                                  <button
                                    key={org.id}
                                    type="button"
                                    onClick={() => {
                                      setTargetOrganizationIds([org.id]);
                                      setOrgSearch("");
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-700"
                                  >
                                    {org.name}
                                    {org.organization_types &&
                                      org.organization_types.length > 0 && (
                                        <span className="ml-2 text-xs text-gray-500">
                                          ({org.organization_types.join(", ")})
                                        </span>
                                      )}
                                  </button>
                                ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Location picker (only when org selected and has locations) */}
                    {targetOrganizationIds.length === 1 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location (optional)
                        </label>
                        {loadingLocations ? (
                          <p className="text-sm text-gray-500">
                            Loading locations...
                          </p>
                        ) : locations.length === 0 ? (
                          <p className="text-xs text-gray-500 italic">
                            This organization has no locations
                          </p>
                        ) : (
                          <select
                            value={targetLocationId}
                            onChange={(e) =>
                              setTargetLocationId(e.target.value as UUID)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">All locations</option>
                            {locations.map((loc) => (
                              <option key={loc.id} value={loc.id}>
                                {loc.name}
                                {loc.city && loc.state
                                  ? ` (${loc.city}, ${loc.state})`
                                  : ""}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Specific Organizations (for MULTIPLE_ORGANIZATIONS) */}
                {redemptionScope === "MULTIPLE_ORGANIZATIONS" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Specific Organizations
                    </label>
                    {loadingOrgs ? (
                      <p className="text-sm text-gray-500">
                        Loading organizations...
                      </p>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={orgSearch}
                          onChange={(e) => setOrgSearch(e.target.value)}
                          placeholder="Search organizations..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                        />
                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                          {organizations
                            .filter(
                              (org) =>
                                org.name
                                  .toLowerCase()
                                  .includes(orgSearch.toLowerCase()) ||
                                targetOrganizationIds.includes(org.id),
                            )
                            .slice(0, 50)
                            .map((org) => (
                              <label
                                key={org.id}
                                className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                                  targetOrganizationIds.includes(org.id)
                                    ? "bg-primary-50"
                                    : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={targetOrganizationIds.includes(
                                    org.id,
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setTargetOrganizationIds([
                                        ...targetOrganizationIds,
                                        org.id,
                                      ]);
                                    } else {
                                      setTargetOrganizationIds(
                                        targetOrganizationIds.filter(
                                          (id) => id !== org.id,
                                        ),
                                      );
                                    }
                                  }}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                  {org.name}
                                </span>
                                {org.organization_types &&
                                  org.organization_types.length > 0 && (
                                    <span className="ml-2 text-xs text-gray-500">
                                      ({org.organization_types.join(", ")})
                                    </span>
                                  )}
                              </label>
                            ))}
                        </div>
                        {targetOrganizationIds.length > 0 && (
                          <p className="text-xs text-gray-600 mt-1">
                            {targetOrganizationIds.length} organization(s)
                            selected
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Validity Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid From
                </label>
                <input
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid To
                </label>
                <input
                  type="date"
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.validTo ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.validTo && (
                  <p className="mt-1 text-sm text-red-600">{errors.validTo}</p>
                )}
              </div>
            </div>

            {/* Voucher Expiry Days - only for VOUCHER_CODE type */}
            {isVoucherCode && (
              <div className="space-y-3">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={voucherExpiresAtOfferValidTo}
                    onChange={(e) =>
                      setVoucherExpiresAtOfferValidTo(e.target.checked)
                    }
                    className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">
                      Expire vouchers on the offer Valid To date
                    </span>
                    <span className="block text-xs text-gray-500">
                      Use this for event-only offers. Every voucher expires at
                      the end of the Valid To date in Central time, regardless
                      of when it is created.
                    </span>
                  </span>
                </label>

                {!voucherExpiresAtOfferValidTo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Voucher Expiry (Days){" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={voucherExpiryDays}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\./g, "");
                        setVoucherExpiryDays(val);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="30"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Each voucher expires this many days after creation
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Redemption Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requiresVoucher"
                  checked={requiresVoucher}
                  onChange={(e) => setRequiresVoucher(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="requiresVoucher"
                  className="ml-2 text-sm text-gray-700"
                >
                  Requires Voucher Code
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Per Member
                </label>
                <input
                  type="number"
                  min="0"
                  value={maxRedemptionsPerMember}
                  onChange={(e) => setMaxRedemptionsPerMember(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Total
                </label>
                <input
                  type="number"
                  min="0"
                  value={maxTotalRedemptions}
                  onChange={(e) => setMaxTotalRedemptions(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Unlimited"
                />
              </div>
            </div>

            {/* Redemption Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Redemption Instructions
              </label>
              <textarea
                value={redemptionInstructions}
                onChange={(e) => setRedemptionInstructions(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="How to redeem this offer..."
              />
            </div>

            {/* Terms and Conditions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terms & Conditions
              </label>
              <textarea
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Offer terms and conditions..."
              />
            </div>

            {/* Active Status and Featured */}
            <div className="flex items-center gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="isActive"
                  className="ml-2 text-sm text-gray-700"
                >
                  Offer is Active
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isFeatured"
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="isFeatured"
                  className="ml-2 text-sm text-gray-700"
                >
                  Featured (show in featured offers section)
                </label>
              </div>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Offer"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
